// Copyright (C) 2023 Cochise Ruhulessin
//
// All rights reserved. No warranty, explicit or implicit, provided. In
// no event shall the author(s) be liable for any claim or damages.
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
import {
  IonButton,
  IonButtons,
  IonContent,
  IonGrid,
  IonItem,
  IonLabel,
  IonModal,
  IonNote,
  IonRow,
  IonText,
  IonToolbar
} from "@ionic/react"
import { useEffect, useState } from "react"
import { useLocales } from "react-localized"
import Select, { SingleValue } from 'react-select'
import { ResponsiveIonCol } from "./ResponsiveIonCol"


interface ModalProps {
  close(): void
  isOpen: boolean
  onSelected(value: any): void
}


const IncoTermsModal: React.FC<ModalProps> = ({
  close,
  isOpen,
  onSelected
}) => {
  const {gettext} = useLocales()
  return (
    <IonModal animated={false} backdropDismiss={false} className="page-modal" isOpen={isOpen}>
      <IonContent>
        <main>
          <IonGrid>
            <IonRow>
              <ResponsiveIonCol>
                <IonText>
                <p>{gettext("Incoterms are a set of 11 internationally recognized rules which define the responsibilities of sellers and buyers.")}</p>
                <p style={{marginBottom: "22px"}}>
                  {gettext("Incoterms specify who is responsible for paying for and managing the shipment, insurance, documentation, customs clearance, and other logistical activities.")}
                </p>
                </IonText>
                <IonText>
                  <Select
                    className="option-select"
                    classNames={{
                      container: (state) => "select-container",
                      control: (state) => "control",
                      indicatorsContainer: (state) => "indicators",
                      menuList: (state) => "menu-list",
                      menu: (state) => "menu",
                      option: (state) => (!!state.isFocused) ? "focus" : "",
                      placeholder: (state) => "placeholder",
                      singleValue: (state) => "current-value",
                      valueContainer: (state) => "value-container"
                    }}
                    classNamePrefix="options"
                    defaultValue={{ label: "Ex works (EXW)", value: "EXW" }}
                    onChange={(option: SingleValue<any>) => {
                      onSelected(option)
                    }}
                    options={[
                      {value: "N_A", label: gettext("Not applied")},
                      {value: "EXW", label: gettext("Ex works (EXW)")},
                      {value: "FCA_A", label: gettext("Free Carrier (FCA-A)")},
                      {value: "FCA_B", label: gettext("Free Carrier (FCA-B)")},
                      {value: "DAP", label: "Delivered At Place (DAP)"},
                      {value: "CIP", label: gettext("Carriage and Insurance Paid To (CIP)")},
                      {value: "CPT", label: gettext("Carriage Paid To (CPT)")},
                    ]}
                  />
                </IonText>
                <IonNote>{gettext("Select the Incoterms applicable to the delivery.")}</IonNote>
              </ResponsiveIonCol>
            </IonRow>
          </IonGrid>
        </main>
      </IonContent>
      <IonToolbar>
        <IonButtons slot="start">
          <IonButton color="medium" onClick={() => {
            close()
          }}>
            {gettext("Cancel")}
          </IonButton>
        </IonButtons>
      </IonToolbar>
    </IonModal>
  )
}

const IncoTermsItem: React.FC<{
  onSelected(value: any): void,
  placeholder: string,
  value: any
}> = ({
  onSelected,
  placeholder,
  value
}) => {
  const {gettext} = useLocales()
  const [isOpen, setIsOpen] = useState<boolean>(false)

  return (
    <>
      <IonItem
        className="clickable"
        detail={true}
        lines="full"
        onClick={() => {
          setIsOpen(true)
        }}
      >
        <IonLabel>
          <h2>{gettext("Incoterms")}</h2>
          <p>{placeholder}</p>
        </IonLabel>
      </IonItem>
      <IncoTermsModal
        close={() => { setIsOpen(false) }}
        isOpen={isOpen}
        onSelected={(value: any) => {
          onSelected(value)
          setIsOpen(false)
        }}
      />
    </>
  )
}


export { IncoTermsItem, IncoTermsModal }